<template>
	<template v-if="!hideHeader && (title || $slots.title || route.meta?.title)">
		<slot
			name="title"
			v-bind="{ items, title: title || route.meta.title, hideHeader }"
		>
			<v-card-title>
				{{
					te(title)
						? t(title, 2)
						: title ||
							t(
								`component.${String(route?.name)}.name`,
								String(route.meta.title)
							)
				}}
			</v-card-title>
		</slot>
	</template>
	<v-container
		fluid
		v-if="
			useFilters ||
			useSearch ||
			(exportable && $vuetify.display.mdAndUp) ||
			$slots.selectedActions
		"
	>
		<div class="d-flex align-center ga-2 py-2">
			<slot
				v-if="useFilters && (filters.length || $slots.filters)"
				name="filters"
				v-bind="{ filtersUsed: filters }"
			>
				<Filters
					:name="tableId"
					:filters="filters.map((filter) => filter.definition)"
				></Filters>
			</slot>
			<v-text-field
				v-if="useSearch"
				v-model.trim="searchValue"
				class="flex-grow-1"
				append-inner-icon="fal fa-search"
				:data-test="`${dataTest}--searchfield`"
				clearable
				density="compact"
				hide-details
				:label="t('common.search')"
				single-line
				variant="solo"
			></v-text-field>
			<div class="d-flex align-center flex-wrap ga-2 ml-auto">
				<slot
					name="selectedActions"
					v-bind="{ selected: model || [], tableId: `${tableId}--datatable` }"
				></slot>
			</div>
			<TableExport
				v-if="exportable && $vuetify.display.mdAndUp"
				class="ml-2"
				:page-count="items?.length || 0"
				:total-count="itemCount || 0"
				:selected-count="(model?.length as number) || 0"
				:has-subtable="!!definition?.multiple"
				:get-export-data="buildExportData"
				:data-test="`${tableId}--export`"
			></TableExport>
		</div>
		<v-row dense no-gutters>
			<v-col v-if="useFilters">
				<ActiveFilters
					v-if="$vuetify.display.smAndUp"
					:name="tableId"
				></ActiveFilters>
			</v-col>
		</v-row>
	</v-container>

	<TablePresentation
		v-if="presentation === 'table'"
		v-model="model"
		:items="items"
		:headers="headers"
		:loading="isOnline ? isLoading : isLoadingOffline"
		:item-count="itemCount"
		:page="page"
		:items-per-page="itemsPerPage"
		:items-per-page-options="paginate.rows"
		:sort-by="sortByValues"
		:definition="{ ...definition, ...{ child: childUsed } }"
		:return-object="returnObject"
		v-model:expanded="expandedItems"
		:use-select="useSelect && $vuetify.display.smAndUp"
		:error="error"
		:use-place-holder-data="usePlaceHolderData"
		:enable-offline-mode="enableOfflineMode"
		:cached-items="cachedItemsRef"
		:name="tableId"
		v-bind="$attrs"
		:local-search="searchValue"
		:data-test="dataTest"
		@update:page="handlePageChange"
		@update:items-per-page="handleItemsPerPageChange"
		@update:sort-by="handleSortChange"
		@navigate-to-child="navigateToChild"
		ref="tableRef"
	>
		<template v-for="(_, slotName) in $slots" #[slotName]="slotData">
			<slot :name="slotName" v-bind="slotData"></slot>
		</template>
	</TablePresentation>

	<CardPresentation
		v-else-if="presentation === 'cards'"
		v-model="model"
		:cached-items="cachedItemsRef"
		:items="items"
		:loading="isOnline ? isLoading : false"
		:item-count="itemCount"
		:sort-by="sortByValues"
		:paginate="paginate"
		:page="page"
		:name="tableId"
		:items-per-page="itemsPerPage"
		:error="isOnline ? error : false"
		:data-test="dataTest"
		@update:page="handlePageChange"
		@update:items-per-page="handleItemsPerPageChange"
		@update:sort-by="handleSortChange"
		@filter-save="filterSave"
	>
		<template #card-header="cardHeaderProps">
			<slot
				name="card-header"
				v-bind="{ ...cardHeaderProps, headers, title }"
			></slot>
		</template>
		<template #card-item="slotProps">
			<slot name="card-item" v-bind="{ ...slotProps, tableId }"></slot>
		</template>
	</CardPresentation>

	<ListPresentation
		v-else-if="presentation === 'list'"
		class="pa-4"
		:items="items"
		:item-count="itemCount"
		:page="page"
		:items-per-page="itemsPerPage"
		:items-per-page-options="paginate.rows"
		:loading="isLoading"
		:error="error"
		:hide-header="hideHeader"
		:title="title"
		:name="tableId"
		:use-place-holder-data="usePlaceHolderData"
		:data-test="dataTest"
		@update:page="handlePageChange"
		@update:items-per-page="handleItemsPerPageChange"
	>
		<template #list-items="slotProps">
			<slot name="list-items" v-bind="slotProps"></slot>
		</template>

		<template #loading v-if="$slots.loading">
			<slot name="loading"></slot>
		</template>
		<template #error="slotProps" v-if="$slots.error">
			<slot name="error" v-bind="slotProps"></slot>
		</template>
		<template #empty v-if="$slots.empty">
			<slot name="empty"></slot>
		</template>
	</ListPresentation>
</template>

<script lang="ts" setup>
import type {
	DataTableDefinition,
	DataTableDefinitionChild,
	DataTableHeader,
	DataTablePaginate,
	SortItem,
} from '@/types/table.ts'
import type { FilterDefinition } from '@/constants/filters'
import { useTable } from 'composables/useTable.ts'
import { isEqual } from 'lodash'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CardPresentation from './CardPresentation.vue'
import ListPresentation from './ListPresentation.vue'
import TablePresentation from './TablePresentation.vue'
import TableExport from './TableExport.vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/system'
import Filters from '@/components/templates/Datatable/Filters.vue'
import ActiveFilters from 'templates/Datatable/ActiveFilters.vue'
import { useI18n } from 'vue-i18n'
import {
	describeActiveFilters,
	type ExportContextItem,
	type ExportInput,
	type ExportSummary,
} from 'composables/useTableExport.ts'
import type { ExportScope } from './TableExport.vue'

import columnTypes from 'constants/columnTypes.ts'
import calculatedColumnNames from 'constants/calculatedColumnNames.ts'

type DataTableProps = {
	title?: string

	hideHeader?: boolean
	hideColumns?: string[]

	definition: DataTableDefinition
	endpoint?: string
	module?: string
	propData?: any[]

	params?: Record<string, any>
	filters?: FilterDefinition[]
	child?: DataTableDefinitionChild
	sortBy?: SortItem[]
	paginate?: DataTablePaginate

	useFilters?: boolean
	useSearch?: boolean
	useSelect?: boolean
	useSort?: boolean
	usePlaceHolderData?: boolean

	returnObject?: boolean
	presentation?: 'table' | 'cards' | 'list'
	name?: string
	preloadAllPages?: boolean
	enableOfflineMode?: boolean
	noChildNavigation?: boolean

	exportable?: boolean
	exportTitle?: string
	exportContext?: ExportContextItem[]
	exportFilenameContext?: ReadonlyArray<string | number | null | undefined>
	exportSummary?: ExportSummary
	exportSummaryBuilder?: (rows: any[]) => ExportSummary | undefined
}

const props = withDefaults(defineProps<DataTableProps>(), {
	module: undefined,
	title: undefined,

	hideHeader: false,
	hideColumns: undefined,

	endpoint: undefined,
	propData: undefined,

	params: undefined,
	filters: undefined,
	child: undefined,
	sortBy: undefined,
	paginate: () => ({ perPage: 25, rows: [10, 25, 50, 100] }),

	useFilters: false,
	useSearch: false,
	useSelect: false,
	useSort: false,
	usePlaceHolderData: false,

	returnObject: false,
	presentation: 'table',
	name: '',
	preloadAllPages: false,
	enableOfflineMode: true,
	exportable: false,
	exportTitle: undefined,
	exportContext: undefined,
	exportFilenameContext: undefined,
	exportSummary: undefined,
	exportSummaryBuilder: undefined,
})

const emit = defineEmits(['created'])
const model = defineModel({ type: Array })
const tableRef = ref()

const router = useRouter()
const route = useRoute()
const { isOnline } = storeToRefs(useUserStore())
const isLoadingOffline = ref<boolean>(true)
const cachedItemsRef = ref<any[] | undefined>(undefined)
const localSearch = ref('')

const childUsed = computed(() => props.child ?? props.definition.child)
const dataTest = computed(() => {
	// For paths with numeric IDs (e.g. units-buildings/123/units), extract a
	// stable data-test name by swapping first-path-segment and module.
	const pathMatch = tableId.value.match(/^(\w+)-([\w-]+)\/\d+\/([\w-]+)$/)
	if (pathMatch) {
		return `${pathMatch[2]}-${pathMatch[1]}`
	}
	// All other patterns (simple, slash-without-id, hyphenated) use the raw tableId.
	return tableId.value
})
const { t, te } = useI18n()
const headers = computed(() => {
	const hideColumnsResult =
		props.hideColumns ?? props.definition?.hideColumns ?? []
	const translatedHideColumns = hideColumnsResult.map((el) =>
		te(el) ? t(el) : el
	)
	let columns = props.definition.columns || []

	const baseName = props.name?.split(/[/_]/)[0]?.replace(/s$/, '')

	const entity = props.definition.endpoint
		? props.definition.endpoint?.split(/[/?]/)[0].replace(/s$/, '')
		: props.endpoint
			? props.endpoint?.split('/')?.pop()?.replace(/s$/, '')
			: baseName
	columns = columns.map((col) => {
		const i18nKey = col.i18nKey || col.key
		return {
			...col,
			title: te(`entity.${entity}.fields.${i18nKey}`)
				? t(`entity.${entity}.fields.${i18nKey}`)
				: te(`entity.${entity}.fields.${i18nKey}.name`)
					? t(`entity.${entity}.fields.${i18nKey}.name`)
					: '?',
		}
	})

	if (props.useSelect) {
		// After
		const newHeader: DataTableHeader = {
			title: 'Select',
			key: calculatedColumnNames.select,
			type: columnTypes.selectRow,
		} as DataTableHeader

		columns = [newHeader, ...columns]
	}
	if (!!props.definition?.expand || !!props.definition?.multiple) {
		const newHeader: DataTableHeader = {
			title: t('component.datatable.expand'),
			key: calculatedColumnNames.expand,
			type: columnTypes.expand,
		} as DataTableHeader

		columns = [...columns, newHeader]
	}
	return columns
		?.filter((column) => translatedHideColumns.indexOf(column.title) < 0)
		.map((column) => {
			if (column?.hide) {
				const hideCondition = { [column.hide]: true }

				column.cellProps = {
					...column.cellProps,
					class: {
						...(column.cellProps?.class || {}),
						...hideCondition,
					},
				}
				column.headerProps = {
					...column.headerProps,
					class: {
						...(column.headerProps?.class || {}),
						...hideCondition,
					},
				}
			}
			return column
		})
})
const searchValue = computed({
	get() {
		return isOnline.value ? onlineSearch.value : localSearch.value
	},
	set(value) {
		if (isOnline.value) {
			onlineSearch.value = value
		} else {
			localSearch.value = value
		}
	},
})

const {
	tableId,
	page,
	itemsPerPage,
	search: onlineSearch,
	sortByValues,
	filters,
	expandedItems,
	selectedItems,
	items,
	itemsTotal,
	itemCount,
	isLoading,
	error,
	handlePageChange,
	handleItemsPerPageChange,
	handleSortChange,
	setSelectedItems,
	tableStateStore,
	usePlaceHolderData,
	preloadAllPages,
	allPagesLoaded,
	reload: reloadTable,
	queryRefetch,
	enable,
} = useTable({
	definition: props.definition,
	endpoint: computed(() =>
		props.endpoint ? props.endpoint : props.definition.endpoint
	),
	propData: computed(() => props.propData),
	params: computed(() => props.params ?? props.definition.params),
	filters: props.filters,
	sortBy: props.sortBy,
	paginate: props.paginate,
	name: props.name,
	module: props.module,
	preloadAllPages: props.preloadAllPages,
	usePlaceHolderData: props.usePlaceHolderData,
	disabled: computed(() => isOnline.value === false),
})

emit('created', tableId)

watch(
	() => props.sortBy,
	(val) => {
		if (val && !isEqual(val, sortByValues.value)) {
			sortByValues.value = val
		}
	},
	{ deep: true }
)

watch(
	isOnline,
	(newValue) => {
		nextTick(() => {
			updateCachedItems()
		})
	},
	{ immediate: true }
)

watch(
	model,
	(newValue) => {
		if (newValue && !isEqual(newValue, selectedItems.value)) {
			setSelectedItems(newValue)
		}
	},
	{ deep: true }
)

watch(
	selectedItems,
	(newValue) => {
		if (newValue && !isEqual(newValue, model.value)) {
			model.value = newValue
		}
	},
	{ deep: true }
)

function navigateToChild(item: any) {
	const route = getChildRoute(item)
	if (route) {
		router.push(route)
	}
}

function getChildRoute(item: any) {
	if (!childUsed.value) return null

	const { multiple } = props.definition
	let data = item
	if (multiple && multiple.linkFirstItem !== true) {
		if (multiple.linkItem && data[multiple.linkItem].length === 1) {
			data = data[multiple.linkItem][0]
		} else {
			return null
		}
	}
	return validateChildParams(data, childUsed.value)
}

function validateChildParams(data: any, childUsed: DataTableDefinitionChild) {
	let child = {
		name: childUsed.name,
		params: {},
	}

	if (typeof data === 'undefined') {
		return null
	}

	childUsed.params?.forEach((param) => {
		let paramValue = data
		param.key.split('.').forEach((key) => {
			if (paramValue === null || typeof paramValue === 'undefined') {
				child = undefined
			} else {
				paramValue = paramValue[key]
			}
		})
		if (child) {
			if (typeof paramValue === 'undefined') {
				paramValue = router.currentRoute.value.params[param.value]
			}
			child.params[param.value] = paramValue
		}
	})

	return child
}

function filterSave(value: any) {
	if (!childUsed.value || !childUsed.value?.filter) return

	const { name, filter, endpoint, tableName } = childUsed.value
	const childTableId = `${tableName || name}-${endpoint}`

	tableStateStore.addOrUpdateFilter(childTableId, {
		definition: filter,
		value,
	})

	router.push({ name })
}

async function updateCachedItems() {
	if (isOnline.value === true) {
		cachedItemsRef.value = undefined
		return
	}

	isLoadingOffline.value = true
	enable()
	if (items.value?.length === 0) {
		console.log('Items are empty, need to reload table:', tableId.value)
		try {
			await queryRefetch()
		} catch (error) {
			console.error('Error loading cached items:', error)
		} finally {
			console.log('Reloading table completed for table:', tableId.value)
			isLoadingOffline.value = false
		}
	}

	try {
		if (!allPagesLoaded.value) {
			await preloadAllPages()
		}
		const allCachedData = itemsTotal.value || []

		if (allCachedData.length > 0) {
			cachedItemsRef.value = allCachedData
		} else {
			cachedItemsRef.value = []
		}
	} catch (error) {
		console.error('Error loading cached items:', error)
		cachedItemsRef.value = []
	} finally {
		isLoadingOffline.value = false
		console.log(
			'Loading offline items completed for table:',
			tableId.value,
			isLoadingOffline.value
		)
	}
}

async function buildExportData(scope: ExportScope): Promise<ExportInput> {
	let rows: any[] = []
	if (scope === 'selected') {
		rows = (model.value as any[]) || []
	} else if (scope === 'all') {
		if (!allPagesLoaded.value) {
			await preloadAllPages()
		}
		rows = itemsTotal.value || items.value || []
	} else {
		rows = items.value || []
	}

	const titleSource =
		props.exportTitle ||
		(props.title && te(props.title) ? t(props.title, 2) : props.title) ||
		t(`component.${String(route?.name)}.name`, String(route.meta?.title || ''))

	const summary =
		props.exportSummary ??
		(props.exportSummaryBuilder ? props.exportSummaryBuilder(rows) : undefined)

	// Compute headers for the expand-row subtable (definition.multiple) using
	// the same i18n lookup as the main headers. Entity falls back to the
	// linkItem name so /users (linkItem: 'persons') resolves to entity.person.
	const multipleDef = props.definition?.multiple
	const subHeaders = multipleDef?.columns?.length
		? multipleDef.columns.map((col: any) => {
				const i18nKey = col.i18nKey || col.key
				const subEntity =
					multipleDef.endpoint?.split(/[/?]/)[0]?.replace(/s$/, '') ||
					multipleDef.linkItem?.replace(/s$/, '')
				return {
					...col,
					title: te(`entity.${subEntity}.fields.${i18nKey}`)
						? t(`entity.${subEntity}.fields.${i18nKey}`)
						: te(`entity.${subEntity}.fields.${i18nKey}.name`)
							? t(`entity.${subEntity}.fields.${i18nKey}.name`)
							: i18nKey,
				}
			})
		: undefined

	return {
		title: titleSource || t('component.export.fallbackTitle', 'Export'),
		headers: headers.value,
		rows,
		activeFilters: describeActiveFilters(filters.value, sortByValues.value, t),
		searchTerm: searchValue.value || undefined,
		context: props.exportContext,
		filenameContext: props.exportFilenameContext,
		summary,
		linkItem: props.definition?.multiple?.linkItem,
		subHeaders,
	}
}

// Expose methods for external access
defineExpose({
	tableId,
	isLoading,
	reloadTable: () => {
		reloadTable()
	},
	isOfflineMode: () => {
		return tableRef.value?.isOfflineMode() || false
	},
})
</script>
